import { CutleryIcon } from "@/components/icons/cutlery-icon";
import { HamburgerIcon } from "@/components/icons/hamburger-icon";
import { HomeIcon } from "@/components/icons/home-icon";
import { ProfileIcon } from "@/components/icons/profile-icon";
import { APP_ROUTES } from "@/constants/app-routes";
import { MixpanelEvents } from "@/constants/mixpanel-events";
import { useOpenCoachChat } from "@/features/ai-chat/hooks/use-open-coach-chat";
import { setNextMyPlanEntrySource } from "@/features/meal-plan/hooks/use-my-plan-analytics";
import { cn } from "@/utils/cn";
import { trackMixpanelEvent } from "@/utils/mixpanel";
import { getSafeAreaBottom } from "@/utils/safe-area-bottom";
import { findScrollableElement, scrollToTop } from "@/utils/scroll-to-top";
import { settingsNavResetAtom } from "@/stores/settings-nav.store";
import { useIsKeyboardOpen } from "@/hooks/utils/use-keyboard-open";
import { useUser } from "@clerk/clerk-react";
import { useSetAtom } from "jotai";
import { memo, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AppBottomBarCoachButton } from "./app-bottom-bar-coach-button";
import { AppBottomBarItem } from "./app-bottom-bar-item";
import { showBottomBarPathSet } from "./bottom-bar-paths";

interface NavigationItem {
  key: string;
  path: string;
  label: string;
  icon: React.ReactNode;
}

const navigationItems: NavigationItem[] = [
  {
    key: "home",
    path: APP_ROUTES.OVERVIEW,
    label: "Home",
    icon: <HomeIcon className="h-5 w-5" />,
  },
  {
    key: "my-plan",
    path: APP_ROUTES.MY_PLAN,
    label: "My Plan",
    icon: <CutleryIcon className="h-5 w-5" />,
  },
  {
    key: "profile",
    path: "/profile",
    label: "Profile",
    // ProfileIcon is a stroke icon — neutralise AppBottomBarItem's
    // `[&_path]:fill-current` so it renders as an outline, not a solid blob.
    icon: <ProfileIcon className="h-5 w-5" />,
  },
];

export const AppBottomBar = memo(function AppBottomBar() {
  const { isLoaded } = useUser();
  const location = useLocation();
  const history = useHistory();
  const openCoachChat = useOpenCoachChat();
  const triggerSettingsReset = useSetAtom(settingsNavResetAtom);
  const isKeyboardOpen = useIsKeyboardOpen();

  const shouldShow = useMemo(
    () => showBottomBarPathSet.has(location.pathname),
    [location.pathname],
  );

  if (!isLoaded || !shouldShow || isKeyboardOpen) {
    return null;
  }

  const handleNavigate = (path: string) => {
    if (path === APP_ROUTES.MY_PLAN) {
      const isAlreadyActive = location.pathname === path;
      trackMixpanelEvent(MixpanelEvents.MY_PLAN_TAB_TAPPED, {
        was_already_active: isAlreadyActive,
      });

      if (!isAlreadyActive) {
        setNextMyPlanEntrySource("bottom_nav");
      }
    }

    if (location.pathname === path) {
      scrollToTop(findScrollableElement());
      return;
    }
    history.replace(path);
  };

  return (
    <div className="fixed right-0 bottom-0 left-0 z-50 flex justify-center">
      <div
        className={cn(
          "w-full max-w-[450px] border-t border-[#E4E4E4] bg-white backdrop-blur-sm",
          "px-4 pt-2 shadow-[0_-8px_32px_rgba(24,29,39,0.08)]",
        )}
        style={{
          paddingBottom: getSafeAreaBottom(),
        }}
      >
        {/* items-end keeps every tab label on the same baseline while the
              Coach button's circle rises above the bar. */}
        <div className="flex items-end justify-between">
          {navigationItems.slice(0, 2).map((item) => (
            <AppBottomBarItem
              key={item.key}
              label={item.label}
              icon={item.icon}
              isActive={location.pathname === item.path}
              onClick={() => handleNavigate(item.path)}
            />
          ))}

          <AppBottomBarCoachButton onClick={openCoachChat} />

          {navigationItems.slice(2).map((item) => (
            <AppBottomBarItem
              key={item.key}
              label={item.label}
              icon={item.icon}
              isActive={location.pathname === item.path}
              onClick={() => handleNavigate(item.path)}
            />
          ))}

          <AppBottomBarItem
            label="More"
            icon={<HamburgerIcon className="h-5 w-5" />}
            isActive={location.pathname === APP_ROUTES.SETTINGS_AND_TOOLS}
            onClick={() => {
              triggerSettingsReset((n) => n + 1);
              handleNavigate(APP_ROUTES.SETTINGS_AND_TOOLS);
            }}
          />
        </div>
      </div>
    </div>
  );
});
